import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './api';

/**
 * Workspace team: invitations to, and members of, a project workspace.
 * Backend: cmd/api/project_workspace_team_handlers.go.
 *
 * Membership does not grant project access yet; it is recorded only.
 *
 * Invitation tokens arrive only in the emailed link, as a URL fragment
 * (/invite/#TOKEN). They are never part of a query key, a URL the app
 * navigates to, or a log line, and they are sent to the API only in JSON
 * bodies.
 */
export type WorkspaceRole = 'editor' | 'commentator' | 'viewer';
export type InvitationStatus = 'pending' | 'accepted' | 'declined' | 'revoked' | 'expired';
export type DeliveryStatus = 'sent' | 'not_configured' | 'failed' | 'not_sent' | '';

export const WORKSPACE_ROLES: { value: WorkspaceRole; label: string; hint: string }[] = [
  { value: 'viewer', label: 'Viewer', hint: 'Can view' },
  { value: 'commentator', label: 'Commentator', hint: 'Can view and comment' },
  { value: 'editor', label: 'Editor', hint: 'Can view, comment and run work' },
];

export function roleLabel(role: string): string {
  return WORKSPACE_ROLES.find((r) => r.value === role)?.label ?? role;
}

export interface WorkspaceInvitation {
  id: string;
  workspace_id: string;
  email: string;
  name: string;
  role: WorkspaceRole;
  status: InvitationStatus;
  inviter_email: string;
  send_count: number;
  last_sent_at: string;
  expires_at: string;
  responded_at?: string;
  delivery_status: DeliveryStatus;
  created_at: string;
  updated_at: string;
}

export interface WorkspaceMember {
  workspace_id: string;
  email: string;
  role: WorkspaceRole;
  created_at: string;
}

export interface InvitationDelivery {
  status: DeliveryStatus;
  sent: boolean;
  message: string;
}

export interface InvitationPreview {
  invitation_id: string;
  workspace_name: string;
  inviter_email: string;
  invitee_email: string;
  role: WorkspaceRole;
  status: InvitationStatus;
  expires_at: string;
}

export const workspaceTeamKey = (workspaceId: string) => ['project-workspaces', workspaceId, 'team'] as const;

export function useWorkspaceTeam(workspaceId: string | null | undefined) {
  return useQuery({
    queryKey: workspaceTeamKey(workspaceId ?? ''),
    queryFn: ({ signal }) =>
      apiClient<{ members: WorkspaceMember[] | null; invitations: WorkspaceInvitation[] | null }>(
        `/project-workspaces/${workspaceId}/team`,
        { signal },
      ),
    select: (d) => ({ members: d.members ?? [], invitations: d.invitations ?? [] }),
    enabled: Boolean(workspaceId),
    staleTime: 10_000,
  });
}

type InvitationResult = { invitation: WorkspaceInvitation; delivery: InvitationDelivery };

export function useInviteToWorkspace(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name?: string; email: string; role: WorkspaceRole }) =>
      apiClient<InvitationResult>(`/project-workspaces/${workspaceId}/invitations`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSettled: () => void qc.invalidateQueries({ queryKey: workspaceTeamKey(workspaceId) }),
  });
}

export function useResendWorkspaceInvitation(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) =>
      apiClient<InvitationResult>(`/project-workspaces/${workspaceId}/invitations/${invitationId}/resend`, {
        method: 'POST',
      }),
    onSettled: () => void qc.invalidateQueries({ queryKey: workspaceTeamKey(workspaceId) }),
  });
}

export function useRevokeWorkspaceInvitation(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) =>
      apiClient<{ invitation: WorkspaceInvitation }>(`/project-workspaces/${workspaceId}/invitations/${invitationId}`, {
        method: 'DELETE',
      }),
    onSettled: () => void qc.invalidateQueries({ queryKey: workspaceTeamKey(workspaceId) }),
  });
}

/** Owner removes a closed (accepted, declined, revoked or expired) invitation from the list. */
export function useDeleteWorkspaceInvitation(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) =>
      apiClient<{ removed: boolean }>(`/project-workspaces/${workspaceId}/invitations/${invitationId}/record`, {
        method: 'DELETE',
      }),
    onSettled: () => void qc.invalidateQueries({ queryKey: workspaceTeamKey(workspaceId) }),
  });
}

/** Owner removes an accepted member; their workspace-derived access ends at once. */
export function useRemoveWorkspaceMember(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (email: string) =>
      apiClient<{ removed: boolean }>(`/project-workspaces/${workspaceId}/members/${encodeURIComponent(email)}`, {
        method: 'DELETE',
      }),
    onSettled: () => void qc.invalidateQueries({ queryKey: workspaceTeamKey(workspaceId) }),
  });
}

/** Status of an invitation as the owner should read it (pending past expiry is expired). */
export function effectiveInvitationStatus(inv: Pick<WorkspaceInvitation, 'status' | 'expires_at'>, now = Date.now()) {
  if (inv.status === 'pending' && Date.parse(inv.expires_at) <= now) return 'expired' as const;
  return inv.status;
}

export interface DeliveryFeedback {
  tone: 'success' | 'warning' | 'error';
  title: string;
  detail: string;
}

/**
 * What to tell the owner after a create or resend. `sent` is the only
 * success: anything else says plainly that no email went out.
 */
export function describeDelivery(kind: 'created' | 'resent', d: InvitationDelivery): DeliveryFeedback {
  if (d.sent && d.status === 'sent') {
    return {
      tone: 'success',
      title: kind === 'created' ? 'Invitation created and email sent' : 'Invitation resent',
      detail:
        kind === 'resent'
          ? `${d.message} The previous link no longer works.`
          : `${d.message} It stays pending until the recipient responds.`,
    };
  }
  if (d.status === 'not_configured') {
    return {
      tone: 'warning',
      title: kind === 'created' ? 'Invitation created — no email sent' : 'Invitation renewed — no email sent',
      detail: d.message || 'Outbound email is not configured on this server. Configure SMTP, then use Resend.',
    };
  }
  return {
    tone: 'error',
    title: kind === 'created' ? 'Invitation created, but delivery failed' : 'Resend failed to deliver',
    detail: d.message || 'The email could not be sent. The invitation is still pending; try Resend.',
  };
}

// ---- Recipient side --------------------------------------------------------

const PENDING_INVITE_KEY = 'enso.pendingInvitation';
const TOKEN_RE = /^[A-Za-z0-9_-]{20,128}$/;

/**
 * Takes the token from a `#TOKEN` fragment and keeps it in sessionStorage
 * (this tab only, gone when the tab closes) so it survives the sign-in
 * round-trip. Returns whether a token was found in the fragment. The caller
 * must then remove the fragment from the URL with a replace navigation.
 */
export function captureInvitationToken(hash: string): boolean {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!raw) return false;
  let token = raw;
  try {
    token = decodeURIComponent(raw);
  } catch {
    return false;
  }
  if (!TOKEN_RE.test(token)) return false;
  try {
    sessionStorage.setItem(PENDING_INVITE_KEY, token);
  } catch {
    // Storage blocked: the page still works until a reload, from memory.
    memoryToken = token;
  }
  return true;
}

let memoryToken: string | null = null;

export function pendingInvitationToken(): string | null {
  try {
    const t = sessionStorage.getItem(PENDING_INVITE_KEY);
    if (t && TOKEN_RE.test(t)) return t;
  } catch {
    /* fall through to memory */
  }
  return memoryToken;
}

export function clearPendingInvitation() {
  memoryToken = null;
  try {
    sessionStorage.removeItem(PENDING_INVITE_KEY);
  } catch {
    /* nothing stored */
  }
}

// The token is deliberately absent from the key.
export const INVITATION_PREVIEW_KEY = ['workspace-invitation', 'preview'] as const;

export function useInvitationPreview(token: string | null, enabled: boolean) {
  return useQuery({
    queryKey: INVITATION_PREVIEW_KEY,
    queryFn: () =>
      apiClient<InvitationPreview>('/workspace-invitations/preview', {
        method: 'POST',
        body: JSON.stringify({ token }),
      }),
    enabled: Boolean(token) && enabled,
    retry: false,
    staleTime: Infinity,
    gcTime: 0,
  });
}

export function useRespondToInvitation() {
  return useMutation({
    mutationFn: ({ token, action }: { token: string; action: 'accept' | 'decline' }) =>
      apiClient<{ member?: WorkspaceMember; declined?: boolean; project_access?: string }>(
        `/workspace-invitations/${action}`,
        { method: 'POST', body: JSON.stringify({ token }) },
      ),
  });
}
